import { mergeTopicContent, TOPIC_ENTRY_KIND_BLOCK, TOPIC_ENTRY_KIND_CONTAINER } from '../../courses/topicContent';

// Same default-position idiom as conversations-creator-canvas's
// useConversationCanvas.js (NODE_START_Y + index*gap when no saved x/y
// exists yet) - just vertical instead of horizontal, matching the topic
// timeline's own top-to-bottom reading order.
// Pins render their block's REAL content (BlockPreviewEntry etc. - same as
// the course preview), not a compact summary card, so they can be much
// taller than a fixed card - default vertical spacing is generous to keep
// freshly-opened, never-dragged-yet pins from overlapping each other. Width
// is unconstrained here (CSS gives it a max-width, content decides the
// rest) - PIN_WIDTH/PIN_HEIGHT are informational only (persisted alongside
// x/y for a possible future explicit-resize feature), never used to size
// the actual rendered card.
export const PIN_START_X = 100;
export const PIN_START_Y = 100;
export const PIN_VERTICAL_GAP = 420;
export const PIN_WIDTH = 680;
export const PIN_HEIGHT = null;

const PIN_KIND_BY_ENTRY_KIND = {
   [TOPIC_ENTRY_KIND_BLOCK]: 'topicElement',
   [TOPIC_ENTRY_KIND_CONTAINER]: 'topicContainer',
   exerciseLink: 'topicExerciseTemplate',
};
const CANVAS_TYPE_BY_PIN_KIND = {
   topicElement: 'TOPIC_ELEMENT_PIN',
   topicContainer: 'TOPIC_CONTAINER_PIN',
   topicExerciseTemplate: 'TOPIC_EXERCISE_PIN',
};

function pinTargetId(canvasElement) {
   if (canvasElement.topicElement) return canvasElement.topicElement.id;
   if (canvasElement.topicContainer) return canvasElement.topicContainer.id;
   if (canvasElement.topicExerciseTemplate) return canvasElement.topicExerciseTemplate.id;
   return null;
}

function safeParseConfig(raw) {
   if (!raw) return {};
   try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
   } catch (e) {
      return {};
   }
}

// Builds the unified draggable-node list useNodeDrag.js expects
// ({id, x, y, ...}) from two independent sources: the topic's own content
// (elements/containers/exercise-links, already fetched the same way
// WorkspaceTopicSection.jsx does) and the raw CanvasElement rows saved so
// far. A topic-content entry with no saved CanvasElement yet gets a
// deterministic default position and a synthetic id (`pin-<kind>-<id>`) -
// nothing is POSTed to the backend until the teacher/student actually
// drags it (see decision #2 in the plan), so opening the canvas the first
// time never writes anything.
export function buildCanvasNodes({ elements, containers, exerciseLinks, canvasElements }) {
   const topLevel = mergeTopicContent({ elements, containers, exerciseLinks, containerId: null });

   const pinsByTargetKey = {};
   (canvasElements || []).forEach((ce) => {
      if (!ce.type || !ce.type.endsWith('_PIN')) return;
      const kind = pinTargetId(ce) != null
         ? (ce.topicElement ? 'topicElement' : ce.topicContainer ? 'topicContainer' : 'topicExerciseTemplate')
         : null;
      if (!kind) return;
      pinsByTargetKey[kind + ':' + pinTargetId(ce)] = ce;
   });

   const pinNodes = topLevel.map((entry, index) => {
      const kind = PIN_KIND_BY_ENTRY_KIND[entry.kind];
      const targetId = entry.item.id;
      const saved = pinsByTargetKey[kind + ':' + targetId];
      const defaultX = PIN_START_X;
      const defaultY = PIN_START_Y + index * PIN_VERTICAL_GAP;
      return {
         id: saved ? String(saved.id) : 'pin-' + kind + '-' + targetId,
         renderKind: 'PIN',
         entryKind: entry.kind,
         pinKind: kind,
         targetId,
         targetItem: entry.item,
         x: saved && saved.x != null ? saved.x : defaultX,
         y: saved && saved.y != null ? saved.y : defaultY,
         width: PIN_WIDTH,
         height: PIN_HEIGHT,
         persisted: !!saved,
         elementId: saved ? saved.id : null,
         type: CANVAS_TYPE_BY_PIN_KIND[kind],
      };
   });

   const freeformNodes = (canvasElements || [])
      .filter((ce) => ce.type && !ce.type.endsWith('_PIN'))
      .map((ce) => ({
         id: String(ce.id),
         renderKind: ce.type,
         x: ce.x || 0,
         y: ce.y || 0,
         width: ce.width || null,
         height: ce.height || null,
         color: ce.color || '#4f8ff0',
         config: safeParseConfig(ce.config),
         persisted: true,
         elementId: ce.id,
         type: ce.type,
      }));

   return [...pinNodes, ...freeformNodes];
}
